/**
 * Generic feature-flag contract, shared by every app that embeds AdminViewModal.
 *
 * Deliberately app-agnostic: this file describes what a flag *is* and how the admin view is told
 * about one. Which flags exist, what they mean and where their state lives are the app's business
 * — it declares its own list and hands it in as a prop.
 */

/** One flag as the admin view needs to see it: what to call it, and whether it is on. */
export interface FeatureFlag {
  /** Stable machine key. Travels to the server as-is, so keep it short and do not rename it. */
  key: string;
  /** Shown next to the switch. Already localised by the app if it cares to. */
  label: string;
  /** Optional one-liner under the label, for flags whose name does not carry their meaning. */
  description?: string;
  /** Current value. The app owns this; the admin view only reports changes to it. */
  enabled: boolean;
}

/** Told to the app when the user flips a switch. The app decides what that means. */
export type FeatureFlagChange = (key: string, enabled: boolean) => void;

/**
 * The enabled keys of a flag set, as the single comma-separated header value that carries them to
 * a server. Encode with `featureFlagHeaderValue` and decode with `parseFeatureFlagHeader` so the
 * encoding is stated in exactly one place and the two halves cannot drift apart.
 */
export const FEATURE_FLAG_HEADER = "x-feature-flags";

export const featureFlagHeaderValue = (flags: Record<string, boolean>): string =>
  Object.keys(flags)
    .filter((key) => flags[key])
    .sort()
    .join(",");

/**
 * The inverse. Takes the raw header as a server sees it — a string, the array form a repeated
 * header produces, or nothing at all — and answers the set of enabled keys.
 *
 * An absent, empty or unparseable value yields an empty set, i.e. every flag off. That is also
 * what a caller which is not the browser produces, and off is always the shipped behaviour.
 */
export const parseFeatureFlagHeader = (raw: string | string[] | undefined | null): Set<string> => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== "string" || value.trim() === "") {
    return new Set();
  }
  return new Set(
    value
      .split(",")
      .map((key) => key.trim())
      .filter((key) => key !== ""),
  );
};
